/**
 * ISM Token Exchange Rate System
 *
 * Base rates (before service/product contract adjustments):
 *   1 GLO  = $11.00 USD
 *   3 HALO = 1 GLO  → 1 HALO = $3.6667 USD
 *
 * Base reward schedule:
 *   Like / lightbulb / star rating  →  0.002 GLO   (~$0.022 USD)
 *   New video / media file uploaded  →  $0.20 USD   in GLO or HALO equivalent
 */

// ─── Prices ────────────────────────────────────────────────────────────────

export const GLO_USD  = 11.00;               // 1 GLO in USD
export const HALO_PER_GLO = 3;               // 3 HALO = 1 GLO
export const HALO_USD = GLO_USD / HALO_PER_GLO; // 1 HALO = $3.6667 USD

// ─── Base Reward Amounts (in token units) ──────────────────────────────────

/** GLO awarded for a like, lightbulb toggle, or star rating */
export const REWARD_LIKE_GLO   = 0.002;

/** USD value dispensed when a new video/media file is uploaded to the platform */
export const REWARD_UPLOAD_USD = 0.20;

/** Upload reward expressed in GLO */
export const REWARD_UPLOAD_GLO  = REWARD_UPLOAD_USD / GLO_USD;   // ≈ 0.01818 GLO

/** Upload reward expressed in HALO */
export const REWARD_UPLOAD_HALO = REWARD_UPLOAD_USD / HALO_USD;  // ≈ 0.05455 HALO

// ─── Conversion helpers ────────────────────────────────────────────────────

/** Convert a GLO amount to USD */
export function gloToUsd(glo: number): number {
  return glo * GLO_USD;
}

/** Convert a HALO amount to USD */
export function haloToUsd(halo: number): number {
  return halo * HALO_USD;
}

/** Convert a GLO amount to HALO */
export function gloToHalo(glo: number): number {
  return glo * HALO_PER_GLO;
}

/** Convert a HALO amount to GLO */
export function haloToGlo(halo: number): number {
  return halo / HALO_PER_GLO;
}

/** Convert USD to GLO */
export function usdToGlo(usd: number): number {
  return usd / GLO_USD;
}

/** Convert USD to HALO */
export function usdToHalo(usd: number): number {
  return usd / HALO_USD;
}

// ─── Display helpers ───────────────────────────────────────────────────────

/** Format a USD amount like "$0.022" or "$11.00" */
export function fmtUsd(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1)    return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

/** Format a token amount with up to 6 sig-figs */
export function fmtToken(amount: number, decimals = 4): string {
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

/** Parse a raw display balance string like "1,234.56" → number, returns 0 if not parseable */
export function parseBalance(raw: string): number {
  if (!raw || raw === "—") return 0;
  const n = parseFloat(raw.replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
}
